import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import ContactForm from "./components/ContactForm";
import FaqAccordion from "./components/FaqAccordion";
import { categories, faqs, siteIntro, testimonials } from "@/mocks/gossip";
import { usePageMeta } from "@/hooks/usePageMeta";

const principles = [
  {
    icon: "ri-focus-3-line",
    title: "只保留现场本身",
    text: "不添加解说、不做情绪化字幕，让画面自己说话。每一位观众都可以有自己的判断。",
  },
  {
    icon: "ri-shield-check-line",
    title: "不做人物评价",
    text: "我们记录的是场景与氛围，不对任何出场人物进行评价、排名或标签化描述。",
  },
  {
    icon: "ri-time-line",
    title: "标注时间与时长",
    text: "每段内容都标注拍摄日期、时长与来源分类，让你在点开之前就知道会看到什么。",
  },
  {
    icon: "ri-scissors-cut-line",
    title: "不拼接不夸张",
    text: "拒绝把不同场景剪在一起制造话题，原始素材有哪些内容，页面就呈现哪些内容。",
  },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 瓜田影像社 | 视频吃瓜内容原则与投稿方式",
    description:
      "瓜田影像社是专注于视频吃瓜现场内容的聚合站，介绍我们的品牌定位、内容原则、用户口碑、常见问题与投稿联系方式，坚持不评价人物、不拼接不夸张。",
    keywords: "视频吃瓜,关于瓜田影像社,视频吃瓜内容原则,现场视频投稿,视频吃瓜常见问题",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于瓜田影像社",
        description: "介绍瓜田影像社的品牌定位、内容原则与联系方式。",
        url: "https://guatian-live.example.com/about",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteIntro.brandName,
        url: "https://guatian-live.example.com/about",
        email: siteIntro.email,
        description: "面向娱乐现场内容爱好者的视频吃瓜聚合站，收录实拍、第一现场、完整版、录像、路透与分直拍。",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ],
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full">
        <section className="w-full border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-primary-600 uppercase mb-3">
                  <span className="w-4 h-px bg-primary-500"></span>
                  About Us
                </span>
                <h1 className="font-heading text-3xl md:text-4xl font-bold leading-tight text-foreground-950 text-balance">
                  {siteIntro.slogan}
                </h1>
                <p className="mt-5 text-sm md:text-base leading-relaxed text-foreground-600">
                  {siteIntro.brandName}成立于 2024 年，是一个专注「视频吃瓜」现场内容的聚合站。
                  我们不做围观者，也不做评论者，只想做那个把镜头稳稳架在现场旁边的人。
                  从音乐节后台到颁奖礼红毯，从剧组夜戏到演唱会安可，凡是被热议的瞬间，我们都尽量找到最接近第一视角的那一帧。
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 cursor-pointer transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-play-circle-line w-4 h-4 flex items-center justify-center"></i>
                    去看今日热榜
                  </Link>
                  <Link
                    to="/about#contact"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 cursor-pointer transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center"></i>
                    投稿与合作
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="w-full h-64 md:h-80 overflow-hidden rounded-lg bg-secondary-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20quiet%20editing%20suite%20overlooking%20a%20faintly%20lit%20stage%20through%20a%20large%20window%2C%20warm%20amber%20desk%20lamp%20and%20rows%20of%20monitors%20showing%20abstract%20waveforms%2C%20deep%20crimson%20curtain%20edge%20in%20the%20foreground%2C%20cinematic%20editorial%20photography%2C%20warm%20amber%20and%20deep%20crimson%20color%20grading%2C%20moody%20lighting%2C%20shallow%20depth%20of%20field%2C%20no%20recognizable%20faces%2C%20high%20detail%2C%20editorial%20composition&width=900&height=600&seq=gossip-about-01&orientation=landscape"
                    alt="瓜田影像社的内容整理工作场景"
                    title="瓜田影像社 视频吃瓜 内容整理"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section aria-labelledby="position-title" className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <h2 id="position-title" className="font-heading text-2xl font-bold text-foreground-950">
                网站定位
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                我们服务于「想第一时间看到现场」的人。你可能是追热点的内容爱好者，也可能是想补全细节的重度观众，
                在这里都能按分类快速找到想看的内容。
              </p>
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {categories.slice(0, 4).map((cat) => (
                  <div key={cat.slug} className="rounded-lg border border-background-200 bg-background-100 p-5">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-primary-100 text-primary-600">
                      <i className={`${cat.icon} w-5 h-5 flex items-center justify-center`}></i>
                    </span>
                    <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">{cat.name}</h3>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{cat.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="principles" className="w-full border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-14">
            <div className="max-w-2xl mb-8">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-primary-600 uppercase mb-2">
                <span className="w-4 h-px bg-primary-500"></span>
                Principles
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">我们的内容原则</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                热闹的题材更需要克制的处理方式。以下四条，是我们每一段视频上线前都会逐条对照的标准。
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((item, index) => (
                <article key={item.title} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-accent-100 text-accent-700">
                      <i className={`${item.icon} w-5 h-5 flex items-center justify-center`}></i>
                    </span>
                    <span className="font-heading text-2xl font-bold text-background-300">0{index + 1}</span>
                  </div>
                  <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section aria-labelledby="voice-title" className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <h2 id="voice-title" className="font-heading text-2xl font-bold text-foreground-950">
                用户口碑
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                我们很少宣传，更多时候是被观众一句一句推荐出去的。以下是部分长期观众留下的真实反馈。
              </p>
            </div>
            <div className="lg:col-span-8">
              <ul className="flex flex-col gap-4">
                {testimonials.map((item) => (
                  <li key={item.name} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                    <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 font-heading text-base font-bold text-background-50">
                      {item.name.slice(0, 1)}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm leading-relaxed text-foreground-700">{item.quote}</p>
                      <p className="mt-2.5 text-xs text-foreground-500">
                        <span className="font-medium text-foreground-800">{item.name}</span>
                        <span className="mx-1.5 text-background-400">·</span>
                        {item.role}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section id="faq" className="w-full border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-4xl px-4 py-12 md:px-6 md:py-14">
            <div className="text-center mb-8">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-primary-600 uppercase mb-2">
                <span className="w-4 h-px bg-primary-500"></span>
                FAQ
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">常见问题</h2>
              <p className="mt-3 text-sm text-foreground-600">关于分类、画质与投稿，你可能想先了解这些。</p>
            </div>
            <FaqAccordion />
          </div>
        </section>

        <section id="contact" className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-widest text-primary-600 uppercase mb-2">
                <span className="w-4 h-px bg-primary-500"></span>
                Contact
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">投稿与联系</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                如果你手上有一段值得被更多人看到的现场画面，欢迎通过右侧表单提交。
                我们会在人工审核后决定是否收录，并通过邮件回复结果。
              </p>
              <ul className="mt-6 flex flex-col gap-4 text-sm">
                <li className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                  </span>
                  <span className="text-foreground-700">{siteIntro.email}</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                  </span>
                  <span className="text-foreground-700">审核周期通常为 3 个工作日</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-shield-line w-4 h-4 flex items-center justify-center"></i>
                  </span>
                  <span className="text-foreground-700">如涉版权或肖像诉求，请邮件告知，我们会优先处理</span>
                </li>
              </ul>
            </div>
            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}